import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';

function Blog() {
    const [activeCategory, setActiveCategory] = useState('All');
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [subscribedEmail, setSubscribedEmail] = useState('');
    const [isSubscribed, setIsSubscribed] = useState(false);

    const categories = [
        'All',
        'Appliance Care',
        'Deep Cleaning',
        'Beauty & Wellness',
        'Home Safety',
        'Partner Stories'
    ];

    const articles = [
        {
            id: 1,
            title: '10 Essential AC Maintenance Tips Before the Summer Heatwave',
            category: 'Appliance Care',
            author: 'Ramesh Sen',
            authorRole: 'Master HVAC Technician',
            date: 'Oct 24, 2026',
            readTime: '4 min read',
            image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=700&q=80',
            excerpt: 'Learn why regular filter cleaning, coil descaling, and refrigerant pressure checks reduce electricity bills by up to 25% and prevent mid-season breakdowns.',
            content: `
Air conditioning units operate under heavy load during warm seasons. Neglecting routine maintenance can lead to unexpected compressor failures, excessive power consumption, and poor cooling efficiency.

### 1. Clean or Replace Mesh Filters Every 3 Weeks
Dust, pollen, and pet dander rapidly clog the primary intake filters. Wash them gently under running tap water and let them air dry completely before sliding them back into the front panel.

### 2. Clear Obstructions Around the Outdoor Unit (Condenser)
The external compressor requires at least 2 feet of clear breathing clearance on all sides to discharge heat efficiently. Trim overgrown vegetation and wash away accumulated grime.

### 3. Check for Refrigerant Leaks & Coil Icing
If you notice ice forming on the copper tubes or if the blower fan dispenses ambient room temperature air, it typically indicates low gas pressure or a capillary tube restriction.

### 4. Schedule a Professional Foam Wash
Standard surface wiping does not eliminate deep bacterial sludge inside the evaporator fins. A pressurized chemical foam wash cleans deep channels and restores factory-level airflow.
            `,
            serviceLink: '/services?search=AC%20Repair',
            serviceName: 'Book AC Servicing'
        },
        {
            id: 2,
            title: 'The Hidden Health Benefits of Deep Home Sanitization & Cleaning',
            category: 'Deep Cleaning',
            author: 'Priya Mukherjee',
            authorRole: 'Hygiene & Sanitization Specialist',
            date: 'Oct 20, 2026',
            readTime: '5 min read',
            image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=700&q=80',
            excerpt: 'Routine sweeping and mopping leaves behind microscopic dust mites, mold spores, and kitchen grease traps. Here is why periodic deep sanitization changes everything.',
            content: `
Many common allergies, respiratory irritations, and recurring sinus flare-ups originate from unseen microbial hotspots inside our homes.

### Where Dust Mites Flourish
Mattresses, fabric sofas, and thick carpets trap shed skin cells and environmental humidity. Professional vacuuming utilizes industrial suction with HEPA filtration to eliminate 99.9% of trapped allergens.

### Kitchen Degreasing & Exhaust Hygiene
Grease vaporizes and forms a sticky combustible film over chimney baffles and tiled splashbacks. Specialized non-toxic degreasing dissolves hardened oils and eliminates foul odor traps.

### Bathroom Descaling & Grout Sterilization
Hard water contains heavy mineral deposits that stain ceramic tiles and harbor black mold colonies. High-temperature steam sterilization effectively eradicates mold spores without damaging delicate enamel.
            `,
            serviceLink: '/services?search=Cleaning',
            serviceName: 'Book Deep Cleaning'
        },
        {
            id: 3,
            title: 'Salon-Grade Skin & Hair Rituals You Can Easily Maintain at Home',
            category: 'Beauty & Wellness',
            author: 'Ananya Sharma',
            authorRole: 'Senior Cosmetologist',
            date: 'Oct 16, 2026',
            readTime: '6 min read',
            image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=700&q=80',
            excerpt: 'Discover the right way to hydrate, exfoliate, and protect your skin barrier without spending hours traveling through heavy city traffic.',
            content: `
Taking care of your skin and hair does not require enduring stressful traffic or waiting in salon queues. Doorstep wellness has elevated at-home pampering to luxury standards.

### The Double-Cleansing Rule
Begin with an oil-based cleanser or micellar formulation to lift sunscreen and waterproof makeup, followed by a gentle hydrating gel cleanser to purify pores without stripping moisture.

### Safe Exfoliation vs. Micro-Tears
Avoid aggressive walnut scrubs that cause microscopic surface abrasions. Opt for gentle fruit enzyme peels or lactic acid exfoliants twice a week.

### Deep Conditioning Hair Spa at Home
Apply protein-rich hair masks on damp hair, wrap in a warm steamed towel for 20 minutes, and seal with a cool water rinse to lock the cuticles and impart mirror-like shine.
            `,
            serviceLink: '/services?search=Salon',
            serviceName: 'Book Salon at Home'
        },
        {
            id: 4,
            title: 'How Hyperlocal Technicians are Transforming India’s Service Economy',
            category: 'Partner Stories',
            author: 'Vikramaditya Roy',
            authorRole: 'VP Partner Operations',
            date: 'Oct 12, 2026',
            readTime: '4 min read',
            image: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=700&q=80',
            excerpt: 'How Reliep’s smart routing and transparent weekly bank payouts are doubling local technicians’ monthly incomes and fostering micro-entrepreneurship.',
            content: `
For decades, skilled technicians in Indian cities relied on informal word-of-mouth recommendations, unpredictable middlemen, and delayed customer payments.

### Fair Pricing & Direct Payouts
By standardizing rate cards and automating instant weekly payouts directly into verified bank accounts, Reliep guarantees that skilled electricians and plumbers receive fair compensation for honest work.

### Hyperlocal Route Optimization
Instead of commuting 20 kilometers through gridlock, technicians receive booking dispatches clustered within a 5-kilometer radius of their primary location, drastically reducing transit stress and fuel costs.
            `,
            serviceLink: '/become-a-partner',
            serviceName: 'Become a Partner'
        },
        {
            id: 5,
            title: 'Electrical Safety 101: Signs Your Home Wiring Needs Immediate Inspection',
            category: 'Home Safety',
            author: 'Sunil Das',
            authorRole: 'Licensed Master Electrician',
            date: 'Oct 08, 2026',
            readTime: '5 min read',
            image: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=700&q=80',
            excerpt: 'Flickering lights, warm switchplates, or frequent MCB trips? Learn how to identify severe electrical hazards before they trigger fire emergencies.',
            content: `
Modern households operate power-hungry appliances like microwaves, induction cooktops, and high-tonnage ACs on wiring systems originally installed decades ago.

### 1. Frequent Miniature Circuit Breaker (MCB) Trips
An MCB is engineered to cut off electricity during dangerous overloads. If a specific circuit trips repeatedly, avoid simply flipping it back on. The circuit is overloaded or contains an internal ground fault.

### 2. Discoloration or Warmth Around Sockets
If a plug point feels hot to the touch or shows faint brownish scorch marks, the internal terminal screw is loose or the wire gauge is insufficient, creating hazardous electrical arcing.

### 3. Faint Burning Plastic Odor
Never ignore a fishy or burning plastic smell near distribution boards. It indicates that cable insulation is degrading under resistive heat. Call an emergency electrician immediately.
            `,
            serviceLink: '/services?search=Electrician',
            serviceName: 'Book Electrician'
        },
        {
            id: 6,
            title: 'Outstation Driving & City Car Rental: What to Check Before Long Trips',
            category: 'Appliance Care',
            author: 'Manish Verma',
            authorRole: 'Fleet Operations Manager',
            date: 'Oct 03, 2026',
            readTime: '4 min read',
            image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=700&q=80',
            excerpt: 'A comprehensive pre-trip safety checklist covering tire tread depth, coolant top-ups, braking response, and emergency breakdown kits.',
            content: `
Embarking on a weekend highway getaway requires more than just filling up the fuel tank. A rigorous 10-minute vehicle safety inspection ensures complete peace of mind.

### Tire Pressure & Tread Condition
Check cold tire pressure including the spare wheel. Inspect tread depth using the coin test; uneven tread wear often points to wheel misalignment or worn tie rods.

### Fluid Levels (Coolant, Brake Oil, Windshield Wash)
Inspect fluid reservoirs when the engine is completely cold. Never mix incompatible coolant types, and ensure windshield wiper blades leave zero streaks.
            `,
            serviceLink: '/services?search=Transport',
            serviceName: 'Book Driver / Cab'
        }
    ];

    const filteredArticles = activeCategory === 'All'
        ? articles
        : articles.filter(a => a.category === activeCategory);

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (subscribedEmail.trim()) {
            setIsSubscribed(true);
            setSubscribedEmail('');
        }
    };

    return (
        <div style={{ backgroundColor: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Helmet>
                <title>Reliep Blog | Expert Everyday Services & Home Maintenance Guides</title>
                <meta name="description" content="Discover actionable home care guides, AC repair tips, deep cleaning hacks, and technician insights from certified Reliep specialists." />
            </Helmet>

            <Navbar />

            {/* HERO SECTION */}
            <section style={{
                background: 'radial-gradient(circle at 85% 20%, #fbf0fb 0%, #ffffff 65%)',
                padding: '64px 20px 72px',
                borderBottom: '1px solid #f2e4f2',
                textAlign: 'center'
            }}>
                <div style={{ maxWidth: '840px', margin: '0 auto' }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: '#fbf2fb',
                        border: '1px solid #ebd0eb',
                        padding: '6px 16px',
                        borderRadius: '9999px',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: '#850C85',
                        marginBottom: '20px'
                    }}>
                        <span>📚</span>
                        <span>Reliep Knowledge Hub & Insights</span>
                    </div>

                    <h1 style={{
                        fontSize: 'clamp(2.2rem, 4.5vw, 3.2rem)',
                        fontWeight: 800,
                        color: '#0f172a',
                        lineHeight: 1.2,
                        letterSpacing: '-0.8px',
                        marginBottom: '16px'
                    }}>
                        Practical Tips & Guides for{' '}
                        <span style={{
                            color: '#850C85',
                            background: 'linear-gradient(135deg, #850C85 0%, #b81ab8 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Everyday Living
                        </span>
                    </h1>

                    <p style={{
                        fontSize: '1.08rem',
                        color: '#475569',
                        lineHeight: 1.6,
                        margin: '0 auto',
                        maxWidth: '680px'
                    }}>
                        Actionable advice from certified electricians, HVAC pros, cleaning masters, and beauty experts to keep your home running smoothly.
                    </p>
                </div>
            </section>

            {/* FEATURED STORY BANNER */}
            {articles[0] && (
                <section style={{ padding: '60px 20px 20px', backgroundColor: '#ffffff' }}>
                    <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                        <div
                            onClick={() => setSelectedArticle(articles[0])}
                            style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '24px',
                                border: '1.5px solid #f1e2f1',
                                overflow: 'hidden',
                                boxShadow: '0 12px 36px rgba(133, 12, 133, 0.08)',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#850C85';
                                e.currentTarget.style.transform = 'translateY(-4px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#f1e2f1';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={{ height: '360px', position: 'relative', overflow: 'hidden' }}>
                                <img
                                    src={articles[0].image}
                                    alt={articles[0].title}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    top: '16px',
                                    left: '16px',
                                    background: '#850C85',
                                    color: '#ffffff',
                                    padding: '5px 12px',
                                    borderRadius: '8px',
                                    fontSize: '0.75rem',
                                    fontWeight: 800,
                                    letterSpacing: '0.5px'
                                }}>
                                    FEATURED GUIDE
                                </div>
                            </div>

                            <div style={{ padding: '40px 36px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', fontSize: '0.82rem', color: '#64748b' }}>
                                    <span style={{ color: '#850C85', fontWeight: 800, textTransform: 'uppercase' }}>
                                        {articles[0].category}
                                    </span>
                                    <span>•</span>
                                    <span>{articles[0].date}</span>
                                    <span>•</span>
                                    <span>{articles[0].readTime}</span>
                                </div>

                                <h2 style={{
                                    fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                                    fontWeight: 800,
                                    color: '#0f172a',
                                    margin: '0 0 16px',
                                    lineHeight: 1.3
                                }}>
                                    {articles[0].title}
                                </h2>

                                <p style={{ fontSize: '0.96rem', color: '#64748b', lineHeight: 1.6, margin: '0 0 24px' }}>
                                    {articles[0].excerpt}
                                </p>

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                            width: '38px',
                                            height: '38px',
                                            borderRadius: '50%',
                                            background: '#fbf2fb',
                                            color: '#850C85',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 800,
                                            fontSize: '0.9rem'
                                        }}>
                                            {articles[0].author[0]}
                                        </div>
                                        <div>
                                            <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a', display: 'block' }}>
                                                {articles[0].author}
                                            </span>
                                            <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                                                {articles[0].authorRole}
                                            </span>
                                        </div>
                                    </div>

                                    <span style={{
                                        color: '#850C85',
                                        fontWeight: 800,
                                        fontSize: '0.92rem',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        Read Article →
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* CATEGORY TABS & ARTICLES GRID */}
            <section style={{ padding: '60px 20px 80px', backgroundColor: '#faf8fa' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                    {/* Category Filter Pills */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: '10px',
                        marginBottom: '44px'
                    }}>
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                style={{
                                    padding: '8px 20px',
                                    borderRadius: '9999px',
                                    border: activeCategory === cat ? '1.5px solid #850C85' : '1px solid #ebd0eb',
                                    background: activeCategory === cat ? '#850C85' : '#ffffff',
                                    color: activeCategory === cat ? '#ffffff' : '#475569',
                                    fontWeight: 700,
                                    fontSize: '0.88rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: activeCategory === cat ? '0 4px 12px rgba(133, 12, 133, 0.25)' : 'none'
                                }}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    {/* Articles Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: '28px'
                    }}>
                        {filteredArticles.map(article => (
                            <div
                                key={article.id}
                                onClick={() => setSelectedArticle(article)}
                                style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '18px',
                                    border: '1px solid #f1e2f1',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 16px rgba(133, 12, 133, 0.05)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-6px)';
                                    e.currentTarget.style.borderColor = '#850C85';
                                    e.currentTarget.style.boxShadow = '0 16px 32px rgba(133, 12, 133, 0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.borderColor = '#f1e2f1';
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(133, 12, 133, 0.05)';
                                }}
                            >
                                <div style={{ height: '210px', width: '100%', position: 'relative', overflow: 'hidden' }}>
                                    <img
                                        src={article.image}
                                        alt={article.title}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '12px',
                                        left: '12px',
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(4px)',
                                        padding: '4px 10px',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: 800,
                                        color: '#850C85'
                                    }}>
                                        {article.category}
                                    </div>
                                </div>

                                <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '8px' }}>
                                        <span>{article.date}</span>
                                        <span>•</span>
                                        <span>{article.readTime}</span>
                                    </div>

                                    <h3 style={{
                                        fontSize: '1.18rem',
                                        fontWeight: 800,
                                        color: '#0f172a',
                                        margin: '0 0 12px',
                                        lineHeight: 1.35
                                    }}>
                                        {article.title}
                                    </h3>

                                    <p style={{
                                        fontSize: '0.88rem',
                                        color: '#64748b',
                                        lineHeight: 1.55,
                                        margin: '0 0 20px',
                                        flex: 1
                                    }}>
                                        {article.excerpt}
                                    </p>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        paddingTop: '16px',
                                        borderTop: '1px solid #f8eef8'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '50%',
                                                background: '#fbf2fb',
                                                color: '#850C85',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 800,
                                                fontSize: '0.75rem'
                                            }}>
                                                {article.author[0]}
                                            </div>
                                            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                                                {article.author}
                                            </span>
                                        </div>

                                        <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#850C85' }}>
                                            Read More →
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NEWSLETTER SUBSCRIBE */}
            <section style={{ padding: '70px 20px', backgroundColor: '#ffffff', borderTop: '1px solid #f2e4f2' }}>
                <div style={{
                    maxWidth: '820px',
                    margin: '0 auto',
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, #fbf2fb 0%, #f7e6f7 100%)',
                    borderRadius: '24px',
                    padding: '48px 24px',
                    border: '1.5px solid #ebd0eb'
                }}>
                    <span style={{ fontSize: '2rem', display: 'block', marginBottom: '12px' }}>📬</span>
                    <h2 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>
                        Stay Ahead with Reliep Insights
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: '#64748b', maxWidth: '520px', margin: '0 auto 28px', lineHeight: 1.5 }}>
                        Join 25,000+ homeowners who receive our weekly home maintenance checklists, exclusive seasonal discounts, and appliance tips.
                    </p>

                    {isSubscribed ? (
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: '#ffffff',
                            padding: '10px 24px',
                            borderRadius: '9999px',
                            border: '1px solid #28a745',
                            color: '#28a745',
                            fontWeight: 700,
                            fontSize: '0.92rem'
                        }}>
                            <span>✓</span>
                            <span>You are subscribed! Check your inbox for our seasonal checklist.</span>
                        </div>
                    ) : (
                        <form onSubmit={handleSubscribe} style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: '10px',
                            maxWidth: '480px',
                            margin: '0 auto'
                        }}>
                            <input
                                type="email"
                                value={subscribedEmail}
                                onChange={(e) => setSubscribedEmail(e.target.value)}
                                placeholder="Enter your email address"
                                required
                                style={{
                                    flex: '1',
                                    minWidth: '240px',
                                    padding: '12px 18px',
                                    borderRadius: '12px',
                                    border: '1.5px solid #ebd0eb',
                                    outline: 'none',
                                    fontSize: '0.92rem',
                                    background: '#ffffff'
                                }}
                            />
                            <button
                                type="submit"
                                style={{
                                    background: '#850C85',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '12px 24px',
                                    fontWeight: 700,
                                    fontSize: '0.92rem',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(133, 12, 133, 0.25)'
                                }}
                            >
                                Subscribe Free
                            </button>
                        </form>
                    )}
                </div>
            </section>

            {/* ARTICLE READING MODAL */}
            {selectedArticle && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(5px)',
                    zIndex: 99999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '20px',
                        width: '100%',
                        maxWidth: '780px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        padding: '36px',
                        position: 'relative',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
                    }}>
                        <button
                            onClick={() => setSelectedArticle(null)}
                            style={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: '#fbf2fb',
                                color: '#850C85',
                                border: 'none',
                                fontSize: '1.2rem',
                                fontWeight: 800,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            ✕
                        </button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#64748b', marginBottom: '10px' }}>
                            <span style={{ color: '#850C85', fontWeight: 800, textTransform: 'uppercase' }}>
                                {selectedArticle.category}
                            </span>
                            <span>•</span>
                            <span>{selectedArticle.date}</span>
                            <span>•</span>
                            <span>{selectedArticle.readTime}</span>
                        </div>

                        <h2 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a', margin: '0 0 16px', lineHeight: 1.3 }}>
                            {selectedArticle.title}
                        </h2>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f1e2f1' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: '#fbf2fb',
                                color: '#850C85',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: '1rem'
                            }}>
                                {selectedArticle.author[0]}
                            </div>
                            <div>
                                <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a', display: 'block' }}>
                                    {selectedArticle.author}
                                </span>
                                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                    {selectedArticle.authorRole}
                                </span>
                            </div>
                        </div>

                        <div style={{ width: '100%', height: '280px', borderRadius: '14px', overflow: 'hidden', marginBottom: '24px' }}>
                            <img
                                src={selectedArticle.image}
                                alt={selectedArticle.title}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        </div>

                        <div style={{
                            fontSize: '1rem',
                            color: '#334155',
                            lineHeight: 1.7,
                            whiteSpace: 'pre-line',
                            marginBottom: '32px'
                        }}>
                            {selectedArticle.content}
                        </div>

                        {/* CTA in Modal */}
                        <div style={{
                            background: '#fbf2fb',
                            borderRadius: '14px',
                            padding: '20px 24px',
                            border: '1px solid #ebd0eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '14px'
                        }}>
                            <div>
                                <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', display: 'block' }}>
                                    Need professional assistance right now?
                                </span>
                                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                                    Book verified experts in under 30 seconds.
                                </span>
                            </div>

                            <Link
                                to={selectedArticle.serviceLink}
                                onClick={() => setSelectedArticle(null)}
                                style={{
                                    background: '#850C85',
                                    color: '#ffffff',
                                    padding: '10px 20px',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    fontSize: '0.88rem',
                                    textDecoration: 'none',
                                    boxShadow: '0 4px 12px rgba(133, 12, 133, 0.25)'
                                }}
                            >
                                {selectedArticle.serviceName} →
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}

export default Blog;

